import { useMemo, useState } from 'react';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Markdown } from '@/components/ui/markdown';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { cn } from '@/lib/utils';
import { useEmployee } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { pdpStatusLabel, pdpStatusVariant } from '../constants';
import {
  useCreatePdpGoal,
  useDeletePdp,
  useDeletePdpGoal,
  usePdp,
  useUpdatePdpGoal,
} from '../queries';
import type { PDPGoal } from '../api';

const GOAL_STATUS_VARIANT: Record<PDPGoal['status'], 'outline' | 'secondary'> = {
  open: 'outline',
  in_progress: 'secondary',
  done: 'secondary',
};

const GOAL_STATUS_LABEL: Record<PDPGoal['status'], string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
};

export function PdpDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const { data: pdp, isPending, isError, error } = usePdp(publicId);
  const employee = useEmployee(pdp?.employee ?? -1);
  const del = useDeletePdp();

  async function handleDelete() {
    if (!pdp) return;
    await del.mutateAsync(pdp.id);
    navigate('/hrm/pdp');
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/pdp')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const employeeLabel = pdp.employee
    ? employee.data
      ? employeeName(employee.data)
      : `Employee #${pdp.employee}`
    : '—';

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/hrm/pdp')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{pdp.title}</h1>
          <Badge variant={pdpStatusVariant(pdp.status)}>{pdpStatusLabel(pdp.status)}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/hrm/pdp/${pdp.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {pdp.title}?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Employee">{employeeLabel}</Field>
          <Field label="Period">{pdp.period || '—'}</Field>
          <Field label="Status">{pdpStatusLabel(pdp.status)}</Field>
          <div className="sm:col-span-3">
            <p className="text-xs font-medium uppercase">Summary</p>
            <div className="text-foreground mt-1">
              {pdp.summary ? <Markdown>{pdp.summary}</Markdown> : '—'}
            </div>
          </div>
        </CardContent>
      </Card>

      <PdpGoals pdpId={pdp.id} goals={pdp.goals} />
    </div>
  );
}

function PdpGoals({ pdpId, goals }: { pdpId: number; goals: PDPGoal[] }) {
  const [newGoal, setNewGoal] = useState('');
  const addGoal = useCreatePdpGoal();
  const updateGoal = useUpdatePdpGoal();
  const removeGoal = useDeletePdpGoal();

  const sortedGoals = useMemo(
    () => [...goals].sort((a, b) => a.order - b.order || a.id - b.id),
    [goals],
  );
  const doneCount = sortedGoals.filter((g) => g.status === 'done').length;
  const pct = sortedGoals.length ? Math.round((doneCount / sortedGoals.length) * 100) : 0;

  async function submitGoal(e: React.FormEvent) {
    e.preventDefault();
    const title = newGoal.trim();
    if (!title) return;
    await addGoal.mutateAsync({ pdp: pdpId, title, order: sortedGoals.length + 1 });
    setNewGoal('');
  }

  function cycleStatus(g: PDPGoal) {
    const next: PDPGoal['status'] =
      g.status === 'open' ? 'in_progress' : g.status === 'in_progress' ? 'done' : 'open';
    updateGoal.mutate({ id: g.id, input: { status: next } });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>Goals</CardTitle>
        <span className="text-muted-foreground text-sm">{pct}% done</span>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-1">
          {sortedGoals.length === 0 ? (
            <li className="text-muted-foreground py-2 text-sm">No goals yet.</li>
          ) : (
            sortedGoals.map((g) => (
              <li
                key={g.id}
                className="hover:bg-muted/40 group flex items-center justify-between gap-3 rounded px-2 py-1.5"
              >
                <span
                  className={cn(
                    'truncate text-sm',
                    g.status === 'done' && 'text-muted-foreground line-through',
                  )}
                >
                  {g.title}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => cycleStatus(g)}
                    className="h-7 px-2"
                  >
                    <Badge variant={GOAL_STATUS_VARIANT[g.status]}>
                      {GOAL_STATUS_LABEL[g.status]}
                    </Badge>
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove ${g.title}`}
                    className="opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => removeGoal.mutate(g.id)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </li>
            ))
          )}
        </ul>
        <form onSubmit={submitGoal} className="flex gap-2">
          <Input
            value={newGoal}
            onChange={(e) => setNewGoal(e.target.value)}
            placeholder="Add a goal…"
            aria-label="New PDP goal"
          />
          <Button type="submit" size="sm" disabled={!newGoal.trim() || addGoal.isPending}>
            <Plus className="size-4" /> Add
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
